<script setup lang="ts">
import { Lock } from 'lucide-vue-next';
import { useId } from 'reka-ui';
import { computed, onMounted, ref } from 'vue';

const model = defineModel<string | number | null>();

interface Props {
    type?: string;
    label?: string;
    error?: string;
    id?: string;
    name?: string;
    disabled?: boolean;
    placeholder?: string;
    /** Aide sous le champ, reliée par `aria-describedby`. */
    help?: string;
    /**
     * Champ verrouillé (contrat « Champ verrouillé ») : lecture seule mais focalisable,
     * `aria-disabled`, icône cadenas ; la raison est reliée par `describedby`.
     */
    locked?: boolean;
    /** Id(s) d'une raison ou d'une aide externe, ajoutés à `aria-describedby`. */
    describedby?: string;
}

const props = withDefaults(defineProps<Props>(), {
    type: 'text',
    disabled: false,
    locked: false,
    describedby: undefined,
});

const emit = defineEmits<{
    (e: 'focus'): void;
    (e: 'blur'): void;
}>();

const inputRef = ref<HTMLInputElement | null>(null);
const generatedId = useId();
const inputId = computed(() => props.id || generatedId);

const hasError = computed(() => !!props.error);
const describedBy = computed(
    () =>
        [
            props.describedby ?? null,
            props.help ? `${inputId.value}-help` : null,
            hasError.value ? `${inputId.value}-error` : null,
        ]
            .filter(Boolean)
            .join(' ') || undefined,
);

onMounted(() => {
    if (inputRef.value?.hasAttribute('autofocus')) {
        inputRef.value?.focus();
    }
});

defineExpose({
    focus: () => inputRef.value?.focus(),
});

const baseClasses = computed(() => [
    'w-full rounded-xl border bg-surface px-3 py-2.5 text-sm text-ink placeholder:text-muted',
    'transition-colors duration-150',
    'disabled:cursor-not-allowed disabled:opacity-50',
    'font-sans',
    props.locked ? 'bg-surface-2 pr-9 cursor-default' : '',
    hasError.value
        ? 'border-negative focus:border-negative focus:ring-negative/20'
        : 'border-border focus:border-terracotta focus:ring-terracotta-soft',
]);
</script>

<template>
    <div class="space-y-1.5">
        <label
            v-if="label"
            :for="inputId"
            class="text-text-soft block text-xs font-semibold tracking-[0.08em] uppercase"
        >
            {{ label }}
        </label>

        <div class="relative">
            <input
                :id="inputId"
                :name="name || inputId"
                ref="inputRef"
                :type="type"
                v-model="model"
                :placeholder="placeholder"
                :disabled="disabled"
                :readonly="locked || undefined"
                :aria-disabled="locked ? 'true' : undefined"
                :class="baseClasses"
                :aria-invalid="hasError"
                :aria-describedby="describedBy"
                v-bind="$attrs"
                @focus="emit('focus')"
                @blur="emit('blur')"
            />
            <Lock
                v-if="locked"
                class="text-muted pointer-events-none absolute top-1/2 right-3 h-4 w-4 -translate-y-1/2"
                aria-hidden="true"
            />
        </div>

        <p
            v-if="help"
            :id="`${inputId}-help`"
            class="text-text-soft text-[13px]"
        >
            {{ help }}
        </p>

        <p
            v-if="hasError"
            :id="`${inputId}-error`"
            class="text-negative text-xs"
            role="alert"
        >
            {{ error }}
        </p>
    </div>
</template>
